import { Hammer } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  VisualCrop,
  VisualShape,
  VisualZoom,
} from 'app/frontend/types/custom/VisualPlan'
import { cn } from '@/lib/utils'
import AssignmentEntityChip from '../../AssignmentEntityChip'
import { MAX_ENTITY_COUNT, stageViewAttrs } from '../constants'
import { ResolvedEntityBadge } from '../shapes/entityAssignmentMatching'
import {
  getEntityMarkerPlacement,
  getSharedMarkerRadius,
} from '../shapes/entityMarkerLayout'
import { getEntityNumberStarts } from '../shapes/entityNumbering'
import { isClosedShape } from '../shapes/entityPositions'
import { isIconShape } from '../shapes/iconShapes'
import EventPlanOverridePopover, {
  assignedCharacterIdsForPicker,
  defaultAggregateOptions,
} from '../../../../TeamEvent/components/EventPlanOverridePopover'
import {
  EncounterAssignmentEntityOverride,
  visualEntitySlotKey,
} from '../../../../TeamEvent/components/eventActivityPlanOverrides'
import { ResolvedAssignmentParticipant } from '../../../../TeamEvent/components/eventActivityTimelineResolver'

type Props = {
  shapes: VisualShape[]
  canvasWidth: number
  canvasHeight: number
  zoom: VisualZoom | undefined
  crop?: VisualCrop
  entityAssignments?: Map<number, ResolvedEntityBadge>
  screenId?: string
  canEdit?: boolean
  canEditUnassignedSlots?: boolean
  selectedParticipants?: ResolvedAssignmentParticipant[]
  onOverrideSelect?: (
    slotKey: string,
    override: EncounterAssignmentEntityOverride,
  ) => void
}

type MarkerTrigger = {
  key: string
  slotKey?: string
  left: number
  top: number
  width: number
  height: number
  badge?: ResolvedEntityBadge
  number: number
}

const EntityBadgeTooltipOverlay = ({
  shapes,
  canvasWidth,
  canvasHeight,
  zoom,
  crop,
  entityAssignments,
  screenId,
  canEdit = false,
  canEditUnassignedSlots = true,
  selectedParticipants,
  onOverrideSelect,
}: Props) => {
  const {
    x: stageX,
    y: stageY,
    scaleX: scale,
  } = stageViewAttrs(zoom, crop, canvasWidth, canvasHeight)
  const starts = getEntityNumberStarts(shapes)
  const maxMarkerRadius = getSharedMarkerRadius(
    shapes,
    canvasWidth,
    canvasHeight,
  )
  const editable = Boolean(canEdit && onOverrideSelect && selectedParticipants)
  const showUnassigned = editable && canEditUnassignedSlots

  const toScreen = (canvasX: number, canvasY: number) => ({
    x: canvasX * scale + stageX,
    y: canvasY * scale + stageY,
  })

  const triggers: MarkerTrigger[] = []

  for (const shape of shapes) {
    const start = starts.get(shape.id)
    if (start === undefined) continue

    if (isIconShape(shape)) {
      const badge = entityAssignments?.get(start)
      if (!badge && !showUnassigned) continue
      const pixelWidth = shape.width * canvasWidth
      const pixelHeight = shape.height * canvasHeight
      const center = toScreen(shape.x * canvasWidth, shape.y * canvasHeight)
      triggers.push({
        key: shape.id,
        slotKey: screenId
          ? visualEntitySlotKey(screenId, shape.id, 0)
          : undefined,
        left: center.x - (pixelWidth / 2) * scale,
        top: center.y - (pixelHeight / 2) * scale,
        width: pixelWidth * scale,
        height: pixelHeight * scale,
        badge,
        number: start,
      })
      continue
    }

    if (isClosedShape(shape) && (shape.entity_count ?? 0) > 0) {
      const placement = getEntityMarkerPlacement(
        shape,
        canvasWidth,
        canvasHeight,
        maxMarkerRadius,
      )
      const theta = (placement.rotation * Math.PI) / 180
      const cos = Math.cos(theta)
      const sin = Math.sin(theta)
      const count = Math.min(shape.entity_count ?? 0, MAX_ENTITY_COUNT)

      placement.positions.forEach((pos, index) => {
        if (index >= count) return
        const badge = entityAssignments?.get(start + index)
        if (!badge && !showUnassigned) return
        const canvasX = placement.centerX + pos.x * cos - pos.y * sin
        const canvasY = placement.centerY + pos.x * sin + pos.y * cos
        const center = toScreen(canvasX, canvasY)
        const radius = placement.markerRadius * scale
        triggers.push({
          key: `${shape.id}-${index}`,
          slotKey: screenId
            ? visualEntitySlotKey(screenId, shape.id, index)
            : undefined,
          left: center.x - radius,
          top: center.y - radius,
          width: radius * 2,
          height: radius * 2,
          badge,
          number: start + index,
        })
      })
    }
  }

  if (triggers.length === 0) return null

  return (
    <TooltipProvider delayDuration={100}>
      <div className='pointer-events-none absolute inset-0 z-10'>
        {triggers.map((trigger) => {
          const selectedOverride:
            | EncounterAssignmentEntityOverride
            | undefined = trigger.badge?.characterId
            ? {
                kind: 'participant',
                characterId: trigger.badge.characterId,
              }
            : undefined
          const hit = (
            <span
              className='block h-full w-full rounded-full'
              aria-label={trigger.badge?.name ?? String(trigger.number)}
            />
          )
          const labeledHit = trigger.badge ? (
            <Tooltip>
              <TooltipTrigger asChild>{hit}</TooltipTrigger>
              <TooltipContent className='border-0 bg-transparent p-0 shadow-none'>
                <AssignmentEntityChip
                  index={trigger.badge.index}
                  label={trigger.badge.label ?? trigger.badge.name}
                  className={cn(
                    trigger.badge.isOverridden && 'border-info-foreground',
                  )}
                  endSlot={
                    trigger.badge.isOverridden ? (
                      <Hammer className='h-4 w-4 text-info-foreground' />
                    ) : undefined
                  }
                />
              </TooltipContent>
            </Tooltip>
          ) : (
            hit
          )

          return (
            <div
              key={trigger.key}
              className='pointer-events-auto absolute [&>button]:h-full [&>button]:w-full'
              style={{
                left: trigger.left,
                top: trigger.top,
                width: trigger.width,
                height: trigger.height,
              }}
            >
              {editable &&
              trigger.slotKey &&
              selectedParticipants &&
              (trigger.badge || canEditUnassignedSlots) ? (
                <EventPlanOverridePopover
                  trigger={labeledHit}
                  participants={selectedParticipants}
                  assignedCharacterIds={assignedCharacterIdsForPicker(
                    triggers.map((entry) => entry.badge?.characterId),
                    trigger.badge?.characterId,
                  )}
                  aggregateOptions={defaultAggregateOptions}
                  selectedOverride={selectedOverride}
                  onSelect={(override) =>
                    onOverrideSelect?.(trigger.slotKey as string, override)
                  }
                />
              ) : (
                labeledHit
              )}
            </div>
          )
        })}
      </div>
    </TooltipProvider>
  )
}

export default EntityBadgeTooltipOverlay
